import React, { useId, useState } from "react";
import { Dialog } from "../ui/Dialog";
import { X } from "lucide-react";
import { List, Task, Tag } from "../../types";
import { Subscription } from "../../hooks/useSubscription";
import {
  SettingsTabs,
  GeneralSettings,
  ListsSettings,
  SharedListsSettings,
  TagsSettings,
  AppearanceSettings,
  ShortcutsSettings,
  SubscriptionSettings,
  CalendarSettings,
  EmailSettings,
  AgentsSettings,
  AccountSettings,
  SettingsTab,
} from "./settings";

interface EditingList extends List {
  newName?: string;
}

interface EditingTag extends Tag {
  newName?: string;
}

interface SettingsDialogProps {
  // List props
  lists: List[];
  tasks: Task[];
  editingList: EditingList | null;
  setEditingList: (list: EditingList | null) => void;
  onUpdateList: (oldName: string, newName: string, emoji: string) => void;
  onDeleteListClick: (listName: string) => void;
  onAddList: () => void;
  // Tag props
  tags: Tag[];
  getTagTaskCount: (tagId: string) => number;
  editingTag: EditingTag | null;
  setEditingTag: (tag: EditingTag | null) => void;
  onUpdateTag: (id: string, newName: string, color: string) => void;
  onDeleteTagClick: (tag: Tag) => void;
  onAddTag: () => void;
  // Subscription props
  isPro: boolean;
  subscriptionPlan: "free" | "monthly" | "yearly" | "included";
  subscription: Subscription | null;
  onUpgradeClick: () => void;
  onManageSubscription: () => void;
  // Integration status — drives which integration-gated sections appear in
  // sub-panels (e.g. the Email section under Keyboard Shortcuts). Optional
  // for ease of mounting in tests; defaults to "not connected".
  gmailConnected?: boolean;
  // Dialog props
  onClose: () => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({
  lists,
  tasks,
  editingList,
  setEditingList,
  onUpdateList,
  onDeleteListClick,
  onAddList,
  tags,
  getTagTaskCount,
  editingTag,
  setEditingTag,
  onUpdateTag,
  onDeleteTagClick,
  onAddTag,
  isPro,
  subscriptionPlan,
  subscription,
  onUpgradeClick,
  onManageSubscription,
  gmailConnected = false,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const titleId = useId();

  const handleClose = () => {
    setEditingList(null);
    setEditingTag(null);
    onClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      labelledBy={titleId}
      backdropClassName="z-30"
      panelClassName="w-full max-w-5xl h-[95vh] sm:h-[85vh] sm:max-h-[820px] overflow-hidden flex flex-col"
    >
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 id={titleId} className="text-lg sm:text-xl font-semibold text-warm-text-primary">Settings</h2>
        <button
          onClick={handleClose}
          className="text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1 -m-1"
          aria-label="Close settings"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:gap-6 flex-1 min-h-0">
        <SettingsTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* overscroll-contain: at the end of a tab, the wheel stops here
            instead of moving the board behind the dialog. */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {activeTab === "general" && <GeneralSettings />}

          {activeTab === "lists" && (
            <ListsSettings
              lists={lists}
              tasks={tasks}
              editingList={editingList}
              setEditingList={setEditingList}
              onUpdateList={onUpdateList}
              onDeleteClick={onDeleteListClick}
              onAddList={onAddList}
            />
          )}

          {activeTab === "sharedLists" && (
            <SharedListsSettings lists={lists} tasks={tasks} />
          )}

          {activeTab === "tags" && (
            <TagsSettings
              tags={tags}
              getTagTaskCount={getTagTaskCount}
              editingTag={editingTag}
              setEditingTag={setEditingTag}
              onUpdateTag={onUpdateTag}
              onDeleteClick={onDeleteTagClick}
              onAddTag={onAddTag}
            />
          )}

          {activeTab === "appearance" && <AppearanceSettings />}

          {activeTab === "shortcuts" && (
            <ShortcutsSettings gmailConnected={gmailConnected} />
          )}

          {activeTab === "subscription" && (
            <SubscriptionSettings
              isPro={isPro}
              plan={subscriptionPlan}
              subscription={subscription}
              onUpgradeClick={onUpgradeClick}
              onManageSubscription={onManageSubscription}
            />
          )}

          {activeTab === "calendar" && <CalendarSettings />}

          {activeTab === "email" && <EmailSettings />}

          {activeTab === "agents" && <AgentsSettings />}

          {activeTab === "account" && (
            <AccountSettings
              isPro={isPro}
              onUpgradeClick={onUpgradeClick}
              subscription={subscription}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default SettingsDialog;
